import type { Term } from '../systems/EventBus';

export interface MarkedTextSegment {
  text: string;
  mark?: Term;
}

interface PatternEntry {
  pattern: RegExp;
  term: Term;
}

const PATTERNS: readonly PatternEntry[] = [
  { pattern: /\baging(?:\s+bucket)?\b/gi, term: 'aging-bucket' },
  { pattern: /\b\d{1,3}\+(?=\s|$)/g, term: 'aging-bucket' }, // "90+" alone
  { pattern: /\b\d{1,3}-\d{1,3}\b/g, term: 'aging-bucket' }, // "31-60"
  { pattern: /\bdunning\b/gi, term: 'dunning' },
  { pattern: /\bcollections\s+referral\b/gi, term: 'dunning' },
  { pattern: /\bpast[- ]?due\b/gi, term: 'dunning' },
  { pattern: /\bfinal\s+notice\b/gi, term: 'dunning' },
  { pattern: /\bcash\s*application\b/gi, term: 'cash-application' },
  { pattern: /\bcash\s*app\b/gi, term: 'cash-application' },
  { pattern: /\bremittance(?:\s+advice)?\b/gi, term: 'cash-application' },
  { pattern: /\bshort[- ]?pay(?:s|ed|ing)?\b/gi, term: 'short-pay' },
  { pattern: /\bcredit\s+memo\b/gi, term: 'short-pay' },
  { pattern: /\bdispute(?:s|d|ing)?\b/gi, term: 'dispute' },
  { pattern: /\bpromise[- ]?to[- ]?pay\b/gi, term: 'promise-to-pay' },
  { pattern: /\bPTP\b/g, term: 'promise-to-pay' },
  { pattern: /\bwrite[- ]?off(?:s)?\b/gi, term: 'write-off' },
  { pattern: /\bDSO\b/g, term: 'dso' },
];

interface Hit {
  start: number;
  end: number;
  term: Term;
}

/** Scan text for known AR vocabulary terms and produce a segments array
 *  suitable for renderMarkedText. Overlapping matches resolve to the
 *  earliest-starting hit. */
export const autoMark = (text: string): readonly MarkedTextSegment[] => {
  const hits: Hit[] = [];
  for (const { pattern, term } of PATTERNS) {
    for (const m of text.matchAll(pattern)) {
      const start = m.index ?? 0;
      hits.push({ start, end: start + m[0].length, term });
    }
  }

  hits.sort((a, b) => a.start - b.start || b.end - a.end);
  const filtered: Hit[] = [];
  let cursor = 0;
  for (const h of hits) {
    if (h.start < cursor) continue;
    filtered.push(h);
    cursor = h.end;
  }

  if (filtered.length === 0) return [{ text }];

  const segments: MarkedTextSegment[] = [];
  let pos = 0;
  for (const h of filtered) {
    if (h.start > pos) segments.push({ text: text.slice(pos, h.start) });
    segments.push({ text: text.slice(h.start, h.end), mark: h.term });
    pos = h.end;
  }
  if (pos < text.length) segments.push({ text: text.slice(pos) });

  return segments;
};
